import React from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "@/components/ui/sheet";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Buildings, Sparkle, Warning } from "@phosphor-icons/react";
import CityDistrictPicker from "@/components/CityDistrictPicker";
import { titleCaseTR, toLowerTR, digitsOnly } from "@/lib/textFormat";

const FieldLabel = ({ children, ai }) => (
  <Label className="text-xs uppercase tracking-widest font-bold text-slate-700 flex items-center gap-1.5">
    {children}
    {ai && (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#F7FBE9] text-[#4E7A0F] text-[9px] font-bold uppercase tracking-widest">
        <Sparkle size={8} weight="fill" /> AI
      </span>
    )}
  </Label>
);

const TYPE_OPTIONS = [
  { value: "uretici", label: "Üretici" },
  { value: "bayi", label: "Bayi" },
  { value: "tedarikci", label: "Tedarikçi" },
];

// Uzun ticari unvandan otomatik kısa isim türet
// Örn: "ABC İNŞAAT MALZEMELERİ SAN. VE TİC. LTD. ŞTİ." -> "ABC İNŞAAT MALZEMELERİ"
const deriveShortName = (title) => {
  if (!title) return "";
  let s = String(title).trim();
  // Sonundaki tüzel kişilik ekleri
  const suffixes = [
    /\bA\.?\s*Ş\.?$/i,
    /\bLTD\.?\s*Ş[Tt][İIıi]\.?$/i,
    /\bLIMITED\s+ŞİRKETİ$/i,
    /\bANONİM\s+ŞİRKETİ$/i,
    /\bKOLL?\.?\s*Ş[Tt][İIıi]\.?$/i,
    /\bKOM\.?\s*Ş[Tt][İIıi]\.?$/i,
  ];
  for (const re of suffixes) s = s.replace(re, "").trim();
  // Ortadaki "SAN. VE TİC.", "SAN. TİC." vs.
  s = s.replace(/\b(SAN(AYİ)?\.?|TİC(ARET)?\.?|İNŞ(AAT)?\.?|MÜH(ENDİSLİK)?\.?|VE)\b\.?/gi, " ");
  s = s.replace(/\s{2,}/g, " ").replace(/[,.\-]+$/g, "").trim();
  // Çok kısaldıysa orijinali koru
  if (s.length < 3) return title;
  return s;
};

/**
 * Malzeme Girişi'nden çağrılan hızlı firma kaydı paneli.
 * Props:
 *  open, onOpenChange
 *  pendingInfo: { name, title, tax_office, tax_number, phone, email, website, address }
 *  pendingRole: "issuer" | "recipient"
 *  saveFn(payload) -> Promise<supplier|null>
 *  saving: bool
 */
export default function SupplierQuickCreate({ open, onOpenChange, pendingInfo, pendingRole, onSaved, saving, saveFn }) {
  const [form, setForm] = React.useState({
    name: "", supplier_type: "tedarikci", title: "",
    tax_office: "", tax_number: "", contact_person: "", phone: "", email: "", website: "",
    address: "", city: "", district: "", notes: "",
  });
  const [nameManuallyEdited, setNameManuallyEdited] = React.useState(false);
  const [aiFields, setAiFields] = React.useState(new Set());

  React.useEffect(() => {
    if (open && pendingInfo) {
      const initialTitle = pendingInfo.title || pendingInfo.name || "";
      const derivedName = deriveShortName(initialTitle) || initialTitle;
      const next = {
        name: pendingInfo.name || derivedName || "",
        supplier_type: pendingInfo.supplier_type || (pendingRole === "recipient" ? "tedarikci" : "tedarikci"),
        title: initialTitle,
        tax_office: pendingInfo.tax_office || "",
        tax_number: pendingInfo.tax_number || "",
        contact_person: pendingInfo.contact_person || "",
        phone: pendingInfo.phone || "",
        email: pendingInfo.email || "",
        website: pendingInfo.website || "",
        address: pendingInfo.address || "",
        city: pendingInfo.city || "",
        district: pendingInfo.district || "",
        notes: "",
      };
      setForm(next);
      setNameManuallyEdited(Boolean(pendingInfo.name));
      const marks = new Set();
      ["title","tax_office","tax_number","phone","email","website","address"].forEach(k => {
        if (next[k]) marks.add(k);
      });
      setAiFields(marks);
    }
    if (!open) {
      setAiFields(new Set());
      setNameManuallyEdited(false);
    }
  }, [open, pendingInfo, pendingRole]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onTitleChange = (v) => {
    setForm(f => {
      const next = { ...f, title: v };
      // Ad manuel değiştirilmediyse title'dan otomatik türet
      if (!nameManuallyEdited) {
        next.name = deriveShortName(v) || v;
      }
      return next;
    });
  };

  const onNameChange = (v) => {
    setNameManuallyEdited(true);
    set("name", v);
  };

  const submit = async () => {
    // İsim boşsa title'dan türet (backward-compat)
    const name = (form.name && form.name.trim()) || deriveShortName(form.title) || form.title;
    const tn = digitsOnly(form.tax_number, 11);
    const payload = {
      ...form,
      name: titleCaseTR(name),
      title: titleCaseTR(form.title),
      tax_office: titleCaseTR(form.tax_office),
      contact_person: titleCaseTR(form.contact_person),
      address: titleCaseTR(form.address),
      city: titleCaseTR(form.city),
      district: titleCaseTR(form.district),
      notes: titleCaseTR(form.notes),
      email: toLowerTR(form.email),
      website: toLowerTR(form.website),
      tax_number: tn,
    };
    const created = await saveFn(payload);
    if (created) onSaved?.(created);
  };

  const roleLabel = pendingRole === "recipient" ? "Düzenlenen (Alıcı)" : "Düzenleyen (Satıcı)";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto p-0 flex flex-col">
        <SheetHeader className="p-6 pb-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#1B4776] flex items-center justify-center">
              <Buildings size={22} weight="duotone" className="text-[#95C11F]" />
            </div>
            <div>
              <SheetTitle className="text-xl font-bold text-slate-900 font-heading">
                Yeni Firma Kaydı
              </SheetTitle>
              <SheetDescription className="text-xs text-slate-600 mt-0.5">
                {roleLabel} firma sistemde yok. Bilgileri kontrol edip kaydedin.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-5" data-testid="supplier-sheet">
          {aiFields.size > 0 && (
            <Card className="p-3 rounded-none sm:rounded-sm bg-[#F7FBE9] border border-[#95C11F]/30">
              <div className="flex items-start gap-2">
                <Sparkle size={16} weight="fill" className="text-[#4E7A0F] mt-0.5" />
                <div className="text-xs text-[#3E5F0C]">
                  <strong>{aiFields.size} alan</strong> yapay zeka tarafından otomatik dolduruldu.
                  Lütfen bilgileri kontrol edin.
                </div>
              </div>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FieldLabel>Firma Adı (Kısa)</FieldLabel>
              <Input value={form.name} data-testid="qs-name"
                     onChange={e => onNameChange(e.target.value)}
                     placeholder="Kartlarda gösterilen kısa ad"
                     className="mt-2 rounded-none sm:rounded-sm" />
              <div className="text-[10px] text-slate-500 mt-1.5">
                Ticari unvana yazdıkça otomatik türetilir, isterseniz düzenleyebilirsiniz.
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Firma Tipi</Label>
              <Select value={form.supplier_type} onValueChange={(v) => set("supplier_type", v)}>
                <SelectTrigger className="mt-2 rounded-none sm:rounded-sm" data-testid="qs-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-slate-500 mt-1.5">
                Firma rolü (Muhasebe uyumu için önemli)
              </div>
            </div>
          </div>

          <div>
            <FieldLabel ai={aiFields.has("title")}>Ticari Unvan</FieldLabel>
            <Input value={form.title} data-testid="qs-title"
                   onChange={e => onTitleChange(e.target.value)}
                   placeholder="Örn: ABC İnşaat Malzemeleri Sanayi ve Ticaret A.Ş."
                   className="mt-2 rounded-none sm:rounded-sm" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FieldLabel ai={aiFields.has("tax_office")}>Vergi Dairesi</FieldLabel>
              <Input value={form.tax_office} data-testid="qs-tax-office"
                     onChange={e => set("tax_office", e.target.value)}
                     className="mt-2 rounded-none sm:rounded-sm" />
            </div>
            <div>
              <FieldLabel ai={aiFields.has("tax_number")}>Vergi Kimlik No (VKN)</FieldLabel>
              <Input value={form.tax_number} data-testid="qs-vkn"
                     onChange={e => set("tax_number", digitsOnly(e.target.value, 11))}
                     inputMode="numeric"
                     maxLength={11}
                     placeholder="10 haneli (şirket) / 11 haneli (şahıs)"
                     className="mt-2 rounded-none sm:rounded-sm font-mono" />
              <div className="text-[10px] text-slate-500 mt-1.5">
                VKN ile aynı firma tekrar açılmaz; mevcut kaydı zenginleştirilir.
              </div>
            </div>
            <div>
              <FieldLabel>Yetkili</FieldLabel>
              <Input value={form.contact_person} data-testid="qs-contact-person"
                     onChange={e => set("contact_person", e.target.value)}
                     className="mt-2 rounded-none sm:rounded-sm" />
            </div>
            <div>
              <FieldLabel ai={aiFields.has("phone")}>Telefon</FieldLabel>
              <Input value={form.phone} data-testid="qs-phone"
                     onChange={e => set("phone", e.target.value)}
                     className="mt-2 rounded-none sm:rounded-sm" />
            </div>
            <div>
              <FieldLabel ai={aiFields.has("email")}>E-posta</FieldLabel>
              <Input type="email" value={form.email} data-testid="qs-email"
                     onChange={e => set("email", e.target.value)}
                     className="mt-2 rounded-none sm:rounded-sm" />
            </div>
            <div>
              <FieldLabel ai={aiFields.has("website")}>Web Sitesi</FieldLabel>
              <Input value={form.website} data-testid="qs-website"
                     onChange={e => set("website", e.target.value)}
                     placeholder="www.firmaadi.com"
                     className="mt-2 rounded-none sm:rounded-sm" />
            </div>
          </div>

          <div>
            <FieldLabel ai={aiFields.has("address")}>Adres</FieldLabel>
            <Textarea value={form.address} data-testid="qs-address"
                      onChange={e => set("address", e.target.value)}
                      rows={3}
                      className="mt-2 rounded-none sm:rounded-sm" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">İl / İlçe</Label>
            <div className="mt-2">
              <CityDistrictPicker
                city={form.city}
                district={form.district}
                onChange={(v) => setForm(f => ({ ...f, city: v.city, district: v.district }))}
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Notlar</Label>
            <Textarea value={form.notes} onChange={e => set("notes", e.target.value)}
                      rows={2}
                      className="mt-2 rounded-none sm:rounded-sm" />
          </div>

          {!form.title && !form.name && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
              <Warning size={14} weight="fill" className="mt-0.5" />
              Ticari unvan veya firma adı en az bir tanesi zorunludur.
            </div>
          )}
        </div>

        <SheetFooter className="p-6 border-t border-slate-200 bg-white flex flex-row sm:justify-start sm:space-x-0 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  className="rounded-none sm:rounded-sm border-slate-300" data-testid="qs-cancel">
            İptal
          </Button>
          <Button onClick={submit} disabled={(!form.title && !form.name) || saving} data-testid="qs-save"
                  className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white font-bold">
            {saving ? "Kaydediliyor..." : "Kaydet ve Kullan"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
